/**
 * Opciones y variantes (CLAUDE.md §23): talla y color, y la combinación concreta
 * que se vende.
 *
 * Un producto declara sus **opciones** («Talla: S, M, L») y sus **variantes**, que
 * son las combinaciones que de verdad existen, cada una con su precio y su stock.
 * No todas las combinaciones tienen por qué existir: puede haber «Rojo·M» y no
 * «Rojo·S», y el tema tiene que poder decirlo sin adivinar.
 */

export type OpcionDeProducto = { nombre: string; valores: string[] }

export type Variante = {
  /** El identificador estable de la combinación: `color:rojo|talla:m`. */
  clave: string
  sku?: null | string
  /** Precio propio en la unidad del panel. Si no lo tiene, manda el del producto. */
  precio?: null | number
  precioAntes?: null | number
  stock?: null | number
}

export type ProductoConVariantes = {
  opciones?: null | OpcionDeProducto[]
  variantes?: null | Variante[]
  precio?: null | number
  precioAntes?: null | number
  stock?: null | number
  sku?: null | string
}

const limpio = (valor: string): string =>
  valor
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

/**
 * La clave de una combinación. **Se ordena por el nombre de la opción** a
 * propósito: «talla M, color rojo» y «color rojo, talla M» son la misma variante, y
 * si la clave dependiera del orden en que se eligieron habría dos filas de stock
 * para lo mismo.
 */
export const claveDeVariante = (elegidas: Record<string, string>): string =>
  Object.entries(elegidas)
    .map(([nombre, valor]) => [limpio(nombre), limpio(valor)] as const)
    .filter(([nombre, valor]) => nombre && valor)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([nombre, valor]) => `${nombre}:${valor}`)
    .join('|')

/** Todas las combinaciones posibles de las opciones, en un orden estable. */
export const combinaciones = (opciones: OpcionDeProducto[]): Array<{ clave: string; elegidas: Record<string, string> }> => {
  if (opciones.length === 0) return []
  let acumulado: Array<Record<string, string>> = [{}]
  for (const opcion of opciones) {
    const siguiente: Array<Record<string, string>> = []
    for (const parcial of acumulado) for (const valor of opcion.valores) siguiente.push({ ...parcial, [opcion.nombre]: valor })
    acumulado = siguiente
    // Un producto con cinco opciones de seis valores son 7776 combinaciones: eso no es
    // un producto con variantes, es un catálogo. Se corta antes de que nadie lo note.
    if (acumulado.length > 200) return acumulado.slice(0, 200).map((elegidas) => ({ clave: claveDeVariante(elegidas), elegidas }))
  }
  return acumulado.map((elegidas) => ({ clave: claveDeVariante(elegidas), elegidas }))
}

export const varianteDe = (producto: ProductoConVariantes, clave: null | string | undefined): null | Variante =>
  clave ? ((producto.variantes ?? []).find((v) => v.clave === clave) ?? null) : null

/** El precio que se cobra: el de la variante si lo tiene, y si no el del producto. */
export const precioDe = (producto: ProductoConVariantes, variante: null | Variante): { precio: null | number; precioAntes: null | number } => ({
  precio: typeof variante?.precio === 'number' ? variante.precio : (producto.precio ?? null),
  precioAntes: typeof variante?.precio === 'number' ? (variante.precioAntes ?? null) : (producto.precioAntes ?? null),
})

/**
 * Cuántas quedan. `null` quiere decir **sin control de stock** —se vende siempre—,
 * que no es lo mismo que cero. Confundirlos deja una tienda vendiendo lo agotado o
 * sin vender lo que hay.
 */
export const stockDe = (producto: ProductoConVariantes, variante: null | Variante): null | number => {
  if (variante) return typeof variante.stock === 'number' ? variante.stock : null
  return typeof producto.stock === 'number' ? producto.stock : null
}

export const skuDe = (producto: ProductoConVariantes, variante: null | Variante): null | string => variante?.sku || producto.sku || null

/** Si hay opciones, elegir una variante no es opcional: sin ella no se sabe qué se vende. */
export const exigeVariante = (producto: ProductoConVariantes): boolean => (producto.opciones ?? []).length > 0

export type ProblemaDeVariante = { clave: string; motivo: string }

/** Qué está mal en las variantes de un producto, al guardarlo. */
export const revisarVariantes = (producto: ProductoConVariantes): ProblemaDeVariante[] => {
  const problemas: ProblemaDeVariante[] = []
  const posibles = new Set(combinaciones(producto.opciones ?? []).map((c) => c.clave))
  const vistas = new Set<string>()
  for (const variante of producto.variantes ?? []) {
    const clave = String(variante.clave ?? '')
    if (!clave) problemas.push({ clave, motivo: 'Una variante no tiene combinación.' })
    else if (vistas.has(clave)) problemas.push({ clave, motivo: `La combinación «${clave}» está dos veces.` })
    else if (posibles.size > 0 && !posibles.has(clave)) problemas.push({ clave, motivo: `La combinación «${clave}» no sale de las opciones de este producto.` })
    vistas.add(clave)
    if (typeof variante.stock === 'number' && variante.stock < 0) problemas.push({ clave, motivo: `El stock de «${clave}» no puede ser negativo.` })
  }
  return problemas
}
